import React, { useRef, useEffect, useCallback } from 'react';
import useNotificationStore from '../store/notificationStore';
import { getWebSocketManager } from '../services/websocket';

const ICON_MAP = {
  alert: { icon: '!', color: '#ef4444' },
  deploy: { icon: 'D', color: '#8b5cf6' },
  task: { icon: 'T', color: '#3b82f6' },
  system: { icon: 'S', color: '#f59e0b' },
  success: { icon: '+', color: '#22c55e' },
  info: { icon: 'i', color: '#667eea' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const NotificationCenter = () => {
  const {
    notifications,
    unreadCount,
    isOpen,
    toggleOpen,
    setOpen,
    markAsRead,
    markAllRead,
    addNotification,
  } = useNotificationStore();

  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, setOpen]);

  // Connect to WebSocket channels for notifications
  useEffect(() => {
    const ws = getWebSocketManager();
    if (!ws) return;

    const unsubs = [];

    unsubs.push(
      ws.on('alert_notifications', (data) => {
        addNotification({
          type: data.level === 'error' ? 'alert' : data.level === 'warning' ? 'system' : 'info',
          title: data.title || 'System Alert',
          message: data.message,
          project: data.project,
        });
      })
    );

    unsubs.push(
      ws.on('task_update', (data) => {
        if (data.status === 'completed') {
          addNotification({
            type: 'task',
            title: 'Task Completed',
            message: data.title || `Task #${data.id} completed`,
            project: data.project,
          });
        }
      })
    );

    unsubs.push(
      ws.on('deploy_notice', (data) => {
        addNotification({
          type: 'deploy',
          title: 'Deployment',
          message: data.message || `${data.project} deployed`,
          project: data.project,
        });
      })
    );

    return () => unsubs.forEach((fn) => fn?.());
  }, [addNotification]);

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={toggleOpen}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
        style={{
          position: 'relative',
          background: 'rgba(102, 126, 234, 0.15)',
          border: '1px solid rgba(102, 126, 234, 0.3)',
          borderRadius: '10px',
          padding: '8px 10px',
          color: 'white',
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            background: '#ef4444',
            color: 'white',
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            fontSize: '0.65rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: '360px',
            maxHeight: '480px',
            background: 'rgba(10, 14, 26, 0.98)',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: 'white', fontWeight: '600', fontSize: '0.9rem' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '40px 16px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.4)',
                fontSize: '0.85rem',
              }}>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const style = ICON_MAP[n.type] || ICON_MAP.info;
                return (
                  <div
                    key={n.id}
                    onClick={() => markAsRead(n.id)}
                    style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      cursor: 'pointer',
                      background: n.read ? 'transparent' : 'rgba(102, 126, 234, 0.05)',
                      transition: 'background 0.15s',
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'flex-start',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(102, 126, 234, 0.05)';
                    }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: `${style.color}20`,
                      color: style.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}>
                      {style.icon}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <span style={{
                          color: 'white',
                          fontSize: '0.8rem',
                          fontWeight: n.read ? '400' : '600',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {n.title}
                        </span>
                        <span style={{
                          color: 'rgba(255,255,255,0.35)',
                          fontSize: '0.7rem',
                          flexShrink: 0,
                        }}>
                          {timeAgo(n.timestamp)}
                        </span>
                      </div>
                      <div style={{
                        color: 'rgba(255,255,255,0.55)',
                        fontSize: '0.75rem',
                        marginTop: '2px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {n.message}
                      </div>
                      {n.project && (
                        <span style={{
                          display: 'inline-block',
                          marginTop: '4px',
                          padding: '1px 6px',
                          borderRadius: '4px',
                          background: 'rgba(102, 126, 234, 0.15)',
                          color: '#667eea',
                          fontSize: '0.65rem',
                        }}>
                          {n.project}
                        </span>
                      )}
                    </div>

                    {/* Unread dot */}
                    {!n.read && (
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#667eea',
                        flexShrink: 0,
                        marginTop: '8px',
                      }} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
